import React, { useEffect, useMemo, useState } from 'react';
import PageLayout from '../../components/layout/PageLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';
import AddMarketplaceDrawer from './components/AddMarketplaceDrawer.jsx';
import MarketplaceCard from './components/MarketplaceCard.jsx';

const MarketplaceManagement = () => {
  const { user } = useAuth();
  const [marketplaces, setMarketplaces] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const loadMarketplaces = async () => {
    const { data } = await supabaseHelpers?.getUserMarketplaces(user?.id);
    setMarketplaces(data || []);
  };

  useEffect(() => {
    loadMarketplaces();
  }, [user]);

  // Seed de exemplos caso usuário não tenha marketplaces
  useEffect(() => {
    const seedIfEmpty = async () => {
      // Permite seed também para visitante sem login
      if (seeding) return;
      if ((marketplaces || [])?.length > 0) return;
      setSeeding(true);
      const samples = [
        {
          user_id: user?.id || 'guest',
          name: 'Mercado Livre',
          platform_url: 'https://www.mercadolivre.com.br',
          fee_percent: 16.0,
          fixed_fee_per_unit: 0.00,
          active: true,
          notes: 'Taxa média em anúncios clássicos'
        },
        {
          user_id: user?.id || 'guest',
          name: 'Shopee',
          platform_url: 'https://shopee.com.br',
          fee_percent: 12.0,
          fixed_fee_per_unit: 0.00,
          active: true,
          notes: 'Taxa variável por categoria'
        },
        {
          user_id: user?.id || 'guest',
          name: 'Etsy',
          platform_url: 'https://www.etsy.com',
          fee_percent: 6.5,
          fixed_fee_per_unit: 5.00,
          active: false,
          notes: 'Inclui taxa fixa por item'
        }
      ];
      try {
        for (const s of samples) {
          await supabaseHelpers?.createMarketplace(s);
        }
        await loadMarketplaces();
      } finally {
        setSeeding(false);
      }
    };
    seedIfEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, marketplaces?.length]);

  const filteredList = useMemo(() => {
    const term = search?.toLowerCase()?.trim();
    const base = (marketplaces || []);
    const activeFiltered = showInactive ? base : base?.filter(mp => (mp?.active !== false));
    return activeFiltered?.filter(mp => {
      if (!term) return true;
      return (mp?.name || '')?.toLowerCase()?.includes(term) || (mp?.platform_url || '')?.toLowerCase()?.includes(term);
    });
  }, [marketplaces, search, showInactive]);

  const handleCreate = async (mp) => {
    const payload = {
      user_id: user?.id,
      name: mp?.name,
      platform_url: mp?.platform_url,
      fee_percent: parseFloat(mp?.fee_percent) || 0,
      fixed_fee_per_unit: parseFloat(mp?.fixed_fee_per_unit) || 0,
      notes: mp?.notes
    };
    const { error } = await supabaseHelpers?.createMarketplace(payload);
    if (error) {
      alert('Erro ao criar marketplace: ' + error?.message);
      return;
    }
    setDrawerOpen(false);
    loadMarketplaces();
  };

  return (
    <PageLayout
      title="Marketplaces"
      subtitle="Cadastre plataformas com taxas de venda"
      iconName="ShoppingCart"
      headerRight={(
        <Button iconName="Plus" onClick={() => setDrawerOpen(true)}>Adicionar Marketplace</Button>
      )}
    >

      {/* Filtros (visual semelhante ao Filamentos) */}
      <div className="calculator-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <Input
            label="Buscar marketplace"
            placeholder="Digite nome ou URL"
            value={search}
            onChange={(e) => setSearch(e?.target?.value)}
          />
          <div className="flex items-center space-x-2 pt-5">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e?.target?.checked)} />
            <span className="text-sm text-muted-foreground">Ocultar inativos</span>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" iconName="RefreshCw" onClick={loadMarketplaces}>Atualizar lista</Button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredList?.map((mp) => (
          <MarketplaceCard key={mp?.id} marketplace={mp} onUpdated={loadMarketplaces} />
        ))}
        {filteredList?.length === 0 && (
          <div className="calculator-card p-8 text-center text-muted-foreground">Nenhum marketplace cadastrado ainda</div>
        )}
      </div>

      {/* Drawer */}
      <AddMarketplaceDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onCreate={handleCreate} />
    </PageLayout>
  );
};

export default MarketplaceManagement;